import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Footer, Navbar } from "../components";
import { getMovieGenres, getTvGenres, getMoviesByGenre, getTvShowsByGenre } from "../constants/api";
import { Film, Tv } from "lucide-react";

const Genres = () => {
  const [movieGenres, setMovieGenres] = useState([]);
  const [tvGenres, setTvGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("movies");

  useEffect(() => {
    const fetchGenres = async () => {
      setLoading(true);
      try {
        const [movieData, tvData] = await Promise.all([
          getMovieGenres(),
          getTvGenres(),
        ]);
        
        // Fetch representative images for each genre
        const movieGenresWithImages = await Promise.all(
          (movieData?.genres || []).map(async (genre) => {
            try {
              const { getMoviesByGenre } = await import("../constants/api");
              const data = await getMoviesByGenre(genre.id, 1);
              const topMovie = data?.results?.[0];
              return {
                ...genre,
                backdrop_path: topMovie?.backdrop_path,
                poster_path: topMovie?.poster_path,
              };
            } catch (error) {
              return genre;
            }
          })
        );
        
        const tvGenresWithImages = await Promise.all(
          (tvData?.genres || []).map(async (genre) => {
            try {
              const { getTvShowsByGenre } = await import("../constants/api");
              const data = await getTvShowsByGenre(genre.id, 1);
              const topShow = data?.results?.[0];
              return {
                ...genre,
                backdrop_path: topShow?.backdrop_path,
                poster_path: topShow?.poster_path,
              };
            } catch (error) {
              return genre;
            }
          })
        );
        
        setMovieGenres(movieGenresWithImages);
        setTvGenres(tvGenresWithImages);
      } catch (error) {
        console.error("Error fetching genres:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGenres();
  }, []);

  const genres = activeTab === "movies" ? movieGenres : tvGenres;

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      
      <div className="bg-black padding pt-[4rem]">
        {/* Section Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-1 w-12 bg-red"></div>
            <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
              Genres
            </span>
          </div>
          <h2 className="text-white mont font-bold lg:text-[42px] md:text-[36px] text-[28px] mb-1">
            Browse by Genre
          </h2>
          <p className="text-white/60 mont text-[15px]">
            Discover movies and TV shows by genre
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="mb-8">
          <div className="flex items-center gap-4 border-b border-white/10">
            <button
              onClick={() => setActiveTab("movies")}
              className={`px-4 py-2 mont font-medium text-[15px] transition-all duration-300 relative ${
                activeTab === "movies"
                  ? "text-red"
                  : "text-white/60 hover:text-white"
              }`}
            >
              <span className="flex items-center gap-2">
                <Film size={18} />
                Movies
              </span>
              {activeTab === "movies" && (
                <span className="absolute bottom-0 left-0 right-0 h-1 bg-red"></span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("tvshows")}
              className={`px-4 py-2 mont font-medium text-[15px] transition-all duration-300 relative ${
                activeTab === "tvshows"
                  ? "text-red"
                  : "text-white/60 hover:text-white"
              }`}
            >
              <span className="flex items-center gap-2">
                <Tv size={18} />
                TV Shows
              </span>
              {activeTab === "tvshows" && (
                <span className="absolute bottom-0 left-0 right-0 h-1 bg-red"></span>
              )}
            </button>
          </div>
        </div>

        {/* Genres Grid */}
        <div className="mb-12">
          {loading ? (
            <div className="grid lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-4">
              {Array.from({ length: 12 }).map((_, index) => (
                <div
                  key={index}
                  className="h-32 bg-gray/30 rounded-lg animate-pulse"
                ></div>
              ))}
            </div>
          ) : (
            <div className="grid lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-4">
              {genres.map((genre) => (
                <Link
                  key={genre.id}
                  to={`/genres/${activeTab}/${genre.id}/${genre.name}`}
                  className="group relative overflow-hidden rounded-lg h-48 flex flex-col justify-between transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-red/30 border border-white/10 hover:border-red/50"
                >
                  {/* Background Image */}
                  {genre.backdrop_path ? (
                    <>
                      <img
                        src={`https://image.tmdb.org/t/p/w780${genre.backdrop_path}`}
                        alt={genre.name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/40"></div>
                    </>
                  ) : genre.poster_path ? (
                    <>
                      <img
                        src={`https://image.tmdb.org/t/p/w500${genre.poster_path}`}
                        alt={genre.name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/40"></div>
                    </>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-red/20 via-red/10 to-black/80"></div>
                  )}
                  
                  {/* Content */}
                  <div className="relative z-10 p-6 flex flex-col justify-between h-full">
                    <div>
                      <h3 className="text-white mont font-semibold text-[17px] mb-2 group-hover:text-red transition-colors duration-300 drop-shadow-lg">
                        {genre.name}
                      </h3>
                    </div>

                    <div className="flex items-center gap-2 text-white/80 group-hover:text-white transition-colors duration-300">
                      <span className="mont text-xs font-medium">Explore</span>
                      <svg
                        className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Genres;
